/**
 * @file 带标题栏的页面布局组件
 */

import { useTracking } from '@seiue/tracking'
import { nth, last } from '@seiue/util'
import React, { useEffect, useContext } from 'react'
import styled from 'styled-components'

import { LayoutContext } from 'packages/web-layout/context'

import { BackToTop } from './BackToTop'
import { Breadcrumbs, useBreadcrumbs } from './Breadcrumbs'
import { Footer as FooterWrapper, FooterProps } from './Footer'
import { Header, HeaderProps } from './Header'

export interface PageProps {
  children?: React.ReactNode
  // 自定义 header component
  headerComponent?: React.ReactNode

  // 原 Header Prop（除 title 外）
  headerProps?: Omit<HeaderProps, 'title'>

  /*
   * 影响网页名称、header title
   * 不设置 title 则 fallback 到当前路由的 getTitle()
   * 如当前路由是 subRoute，则 fallback 到父路由的 getTitle()
   */
  title?: string | ((title: string) => string)

  footerContentComponent?: React.ReactNode // 底部固定栏
  footerStyle?: React.CSSProperties // 底部栏样式

  // 是否限制最大宽度为 1312px（当 Page 只有一个卡片且内容不是表格时），默认 false
  limitWidth?: boolean
  // 是否展示面包屑，默认 true
  useBreadcrumb?: boolean

  containerStyle?: React.CSSProperties // 包裹 children 的容器的样式
  childrenWrapperStyle?: React.CSSProperties // 布局中 ChildrenWrapper 的样式
  /**
   * 是否展示 header component,默认 false
   */
  useHeaderComponent?: boolean
}

// 带 Footer 距离底部：73 = footer 高度 + 16
const backTopBottomWithFooter = 73

// 不带 Footer 距离底部：32
const backTopBottomWithoutFooter = 32

export const PageContext = React.createContext<{
  Footer: React.FC<FooterProps>
}>({
  Footer: () => null,
})

export const Page: React.FC<PageProps> = ({
  headerComponent,
  headerProps = {},
  title,
  footerContentComponent,
  footerStyle,
  children,
  limitWidth = false,
  containerStyle,
  childrenWrapperStyle,
  useBreadcrumb = true,
  useHeaderComponent = false,
}) => {
  const breadcrumbs = useBreadcrumbs()

  // 当前路由为 subRoute 时，使用 parent route 的 title
  const offset = last(breadcrumbs)?.parentPath ? -2 : -1
  const breadcrumbsTitle = nth(breadcrumbs, offset)?.getTitle?.() || ''
  const finalTitle = !title
    ? breadcrumbsTitle
    : typeof title === 'string'
    ? title
    : title(breadcrumbsTitle)

  // Set webpage title
  useEffect(() => {
    if (finalTitle) window.document.title = finalTitle
  }, [finalTitle])

  const { trackPageView } = useTracking()

  // Track page view
  React.useEffect(() => {
    trackPageView({ documentTitle: finalTitle })
  }, [finalTitle, trackPageView])

  const [hasFooter, setHasFooter] = React.useState(false)

  const Footer = React.useCallback<
    React.FC<{
      style?: React.CSSProperties
      children?: React.ReactNode
    }>
  >(
    (props) => {
      if (!props.children) {
        setHasFooter(false)
        return null
      }

      setHasFooter(true)
      return (
        <FooterWrapper
          limitWidth={limitWidth}
          {...props}
        />
      )
    },
    [limitWidth]
  )

  const { contentContainerEl } = useContext(LayoutContext)

  return (
    <PageContext.Provider value={{ Footer }}>
      <Container
        data-test-id="页面布局"
        hasFooter={hasFooter}
        limitWidth={limitWidth}
        style={containerStyle}
      >
        {useBreadcrumb && <BreadcrumbsStyled breadcrumbs={breadcrumbs} />}
        {useHeaderComponent
          ? headerComponent || (
              <Header
                title={finalTitle}
                {...headerProps}
              />
            )
          : null}
        <ChildrenWrapper
          style={childrenWrapperStyle}
          $hasBreadcrumb={breadcrumbs.length > 1 && useBreadcrumb}
          $hasFooter={!!footerContentComponent}
        >
          {children}
        </ChildrenWrapper>
        {footerContentComponent && (
          <Footer style={footerStyle}>{footerContentComponent}</Footer>
        )}
        <BackToTop
          scrollTarget={contentContainerEl}
          bottom={
            hasFooter ? backTopBottomWithFooter : backTopBottomWithoutFooter
          }
        />
      </Container>
    </PageContext.Provider>
  )
}

const Container = styled.div<{ hasFooter: boolean; limitWidth: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0 20px ${({ hasFooter }) => (hasFooter ? 48 : 24)}px 20px;
  overflow-y: auto;
  ${({ limitWidth }) => limitWidth && 'max-width: 1312px;'}
`

const ChildrenWrapper = styled.div<{
  $hasFooter: boolean
  $hasBreadcrumb: boolean
}>`
  width: 100%;
  flex: 1;
  margin-right: -24px;
  border-radius: 12px;
  /* 通过这样的方式，使滚动条显示在页面的最右侧 */
  margin-left: 20px 9px 20px 20px;
  padding-bottom: ${(props) => (props.$hasFooter ? '24px' : 0)};
  overflow: auto;
  padding-top: ${(props) => (props.$hasBreadcrumb ? 0 : '20px')};

  &&& {
    .sei-route-tab {
      position: absolute;
      width: calc(100% - 40px);
      height: ${(props) =>
        props.$hasBreadcrumb
          ? `calc(100% - 32px - 20px)`
          : `calc(100% - 32px - 10px)`};
      border-radius: 12px;
    }
    .sei-route-tab > div:first-child {
      padding-left: 0;
      padding-top: 0;
      padding-right: 0;
      border-radius: 12px;
      .ant-tabs-nav-list {
        padding-left: 20px;
        display: flex;
        justify-content: flex-start;
        gap: 16px;
      }
      .ant-tabs-nav {
        height: 48px !important;
      }
      .ant-tabs-tab {
        min-width: 0 !important;
        height: 48px;
        padding: 0 !important;
        font-size: 14px;
        font-weight: 400;
        border-bottom: 1px solid transparent;
      }
      .ant-tabs-tab-btn {
        color: ${(props) => props.theme.text._2};
      }
      .ant-tabs-tab-active {
        border-bottom: 2px solid ${(props) => props.theme.brand._1};
        & .ant-tabs-tab-btn {
          color: ${(props) => props.theme.brand._1};
        }
      }
      .ant-tabs-ink-bar-animated {
        display: none;
      }
    }
    .sei-route-tab > div:last-child {
      padding: 12px 20px 20px 20px;
      overflow-y: scroll;
    }
  }
`

const BreadcrumbsStyled = styled(Breadcrumbs)`
  &&& {
    margin: 18px 0 12px 0;
    line-height: 14px;
    padding-left: 5px;
  }
`
